import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import {
  faArrowRight,
  faMoneyBill,
  faPercentage,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'antd';

import { clientStatuses } from '@app/components/RecordModal/components/RecordDetails/data/constants';
import type { TagOption } from '@app/components/RecordModal/components/RecordDetails/types';
import { Tag } from '@app/components/TagsSelect/ui/Tag';
import type { IRecordType } from '@app/types';

import { getRecordDisplayIdentity } from '@pages/Calendar/utils/getRecordDisplayIdentity';

import {
  useAppointmentCardInfoTooltip,
  useAppointmentCardInfoTooltipEffects,
} from '../hooks';
import { stopCardInteraction } from '../utils';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { FC } from 'react';

import '../styles/AppointmentCardInfoTooltip.scss';

interface AppointmentCardInfoTooltipProps {
  record: IRecordType;
  tagOptions: TagOption[];
  canShowContacts: boolean;
}

interface FeatureBadge {
  key: string;
  label: string;
  icon: IconDefinition;
  color?: string;
}

const formatPhone = (phone?: string): string | undefined => {
  if (!phone) {
    return undefined;
  }

  return phone.startsWith('+') ? phone : `+${phone}`;
};

const getFeatureBadges = ({
  clientHasLoyaltyCard,
  clientFutureRecordIds,
  clientServices,
}: {
  clientHasLoyaltyCard: boolean | undefined;
  clientFutureRecordIds: IRecordType['ClientFutureRecordIds'];
  clientServices: NonNullable<IRecordType['ClientServices']>;
}): FeatureBadge[] => {
  const badges: FeatureBadge[] = [];

  if (clientHasLoyaltyCard) {
    badges.push({
      key: 'loyalty-card',
      label: 'Есть карта лояльности',
      icon: faPercentage,
      color: 'rgb(156, 39, 176)',
    });
  }

  if (clientFutureRecordIds?.length) {
    badges.push({
      key: 'future-records',
      label: 'Есть записи в будущем',
      icon: faArrowRight,
    });
  }

  if (clientServices.some(({ UserId }) => Boolean(UserId))) {
    badges.push({
      key: 'upsell',
      label: 'Есть доп. продажи',
      icon: faMoneyBill,
    });
  }

  return badges;
};

export const AppointmentCardInfoTooltip: FC<
  AppointmentCardInfoTooltipProps
> = ({ record, tagOptions, canShowContacts }) => {
  const {
    ClientServices,
    Status,
    Comment,
    IsPaid,
    ClientFutureRecordIds,
    ClientHasLoyaltyCard,
  } = record;
  const clientServices = ClientServices ?? [];

  const statusItem =
    clientStatuses.find((item) => item.status === Status) ?? clientStatuses[0];

  const { clientName, email, phone } = getRecordDisplayIdentity({
    record,
    canShowContacts,
  });
  const formattedPhone = formatPhone(phone);
  const { isPhoneCopyable, isPhoneCopied, resetTimerRef, handleCopyPhone } =
    useAppointmentCardInfoTooltip({ phone: formattedPhone, canShowContacts });

  useAppointmentCardInfoTooltipEffects({ resetTimerRef });
  const copyPhoneLabel = isPhoneCopied
    ? 'Скопировано'
    : 'Скопировать номер телефона';

  const featureBadges = getFeatureBadges({
    clientHasLoyaltyCard: ClientHasLoyaltyCard,
    clientFutureRecordIds: ClientFutureRecordIds,
    clientServices,
  });
  const paymentStatus = IsPaid
    ? 'Визит оплачен'
    : 'Визит не оплачен или оплачен частично';

  return (
    <div className="AppointmentCardInfoTooltip">
      <div className="AppointmentCardInfoTooltip__client">
        <div className="AppointmentCardInfoTooltip__name">{clientName}</div>

        <div className="AppointmentCardInfoTooltip__contacts">
          {!!formattedPhone && !isPhoneCopyable && (
            <span className="AppointmentCardInfoTooltip__contact">
              {formattedPhone}
            </span>
          )}

          {!!formattedPhone && isPhoneCopyable && (
            // Копирует и номер, и иконка: обработчики на обёртке, клик по
            // кнопке всплывает сюда. Без остановки pointer-событий нажатие
            // открыло бы запись, а сдвиг начал бы её перенос.
            <span
              className="AppointmentCardInfoTooltip__phone"
              onPointerDown={stopCardInteraction}
              onPointerUp={stopCardInteraction}
              onClick={handleCopyPhone}
            >
              <span className="AppointmentCardInfoTooltip__contact">
                {formattedPhone}
              </span>

              <Tooltip title={copyPhoneLabel}>
                <button
                  type="button"
                  className="AppointmentCardInfoTooltip__copy-button"
                  aria-label={copyPhoneLabel}
                  data-testid="appointment-card-copy-phone"
                >
                  {isPhoneCopied ? <CheckOutlined /> : <CopyOutlined />}
                </button>
              </Tooltip>
            </span>
          )}

          {!!email && (
            <span className="AppointmentCardInfoTooltip__contact">{email}</span>
          )}
        </div>
      </div>

      <div className="AppointmentCardInfoTooltip__badges">
        <div
          className="AppointmentCardInfoTooltip__status"
          style={{
            borderColor: statusItem.color,
            backgroundColor: statusItem.color,
          }}
        >
          <FontAwesomeIcon
            icon={statusItem.icon}
            className="AppointmentCardInfoTooltip__status-icon"
          />
          <span className="AppointmentCardInfoTooltip__badge-text">
            {statusItem.label}
          </span>
        </div>

        {featureBadges.map(({ key, label, icon, color }) => (
          <div
            key={key}
            className="AppointmentCardInfoTooltip__status AppointmentCardInfoTooltip__status--feature"
          >
            <span className="AppointmentCardInfoTooltip__feature-content">
              <FontAwesomeIcon
                icon={icon}
                className="AppointmentCardInfoTooltip__feature-icon"
                color={color}
              />
              <span
                className="AppointmentCardInfoTooltip__badge-text"
                style={{ color }}
              >
                {label}
              </span>
            </span>
          </div>
        ))}
      </div>

      {clientServices.length > 0 && (
        <div className="AppointmentCardInfoTooltip__services">
          <div className="AppointmentCardInfoTooltip__section-title">
            Услуги
          </div>

          <ul className="AppointmentCardInfoTooltip__services-list">
            {clientServices.map((service) => (
              <li
                key={service.Id}
                className="AppointmentCardInfoTooltip__services-item"
              >
                {service.Name}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="AppointmentCardInfoTooltip__payment-status">
        <span className="AppointmentCardInfoTooltip__label">Оплата</span>
        <span className="AppointmentCardInfoTooltip__value">
          {paymentStatus}
        </span>
      </div>

      {tagOptions.length > 0 && (
        <div className="AppointmentCardInfoTooltip__tags">
          <div className="AppointmentCardInfoTooltip__section-title">Теги</div>

          <div className="AppointmentCardInfoTooltip__tags-row">
            {tagOptions.map(({ backgroundColor, color, value, label }) => (
              <Tag
                key={`${value}-${label}`}
                label={label}
                value={value}
                color={color}
                backgroundColor={backgroundColor}
                closable={false}
              />
            ))}
          </div>
        </div>
      )}

      {!!Comment && (
        <div className="AppointmentCardInfoTooltip__comment">
          <div className="AppointmentCardInfoTooltip__section-title">
            Комментарий
          </div>

          <div className="AppointmentCardInfoTooltip__comment-text">
            {Comment}
          </div>
        </div>
      )}
    </div>
  );
};
